import type { IconComponent } from '../icons/iconTypes';
import { statusIcons } from '../icons/statusIcons';
import type { StatusTone } from '../tokens/statusColorMap';

export interface StatusUiModel {
  label: string;
  tone: StatusTone;
  icon: IconComponent;
  description?: string;
}

function entry(label: string, tone: StatusTone, icon: IconComponent = statusIcons[tone === 'danger' ? 'error' : tone === 'neutral' ? 'neutral' : tone], description?: string): StatusUiModel {
  return { label, tone, icon, description };
}

const paymentStatusMap: Record<string, StatusUiModel> = {
  unmatched: entry('Не сопоставлено', 'danger', statusIcons.error),
  automatched: entry('Авто', 'success', statusIcons.success),
  manualmatched: entry('Вручную', 'info', statusIcons.info),
  preview: entry('На проверке', 'warning', statusIcons.warning),
  readytosend: entry('Готово к отправке', 'success', statusIcons.success),
  sent: entry('Отправлено', 'success', statusIcons.success),
  senderror: entry('Ошибка отправки', 'danger', statusIcons.error),
  failed: entry('Ошибка', 'danger', statusIcons.error),
  lowconfidence: entry('Низкая уверенность', 'warning', statusIcons.warning),
  pending: entry('Ожидание', 'neutral', statusIcons.pending),
  queued: entry('В очереди', 'info', statusIcons.info),
  processing: entry('Обработка', 'info', statusIcons.info),
  posted: entry('Проведено', 'success', statusIcons.success),
  reconciled: entry('Сверено', 'success', statusIcons.success),
};

const postingStatusMap: Record<string, StatusUiModel> = {
  draft: entry('Черновик', 'neutral', statusIcons.pending),
  pending: entry('Ожидание', 'neutral', statusIcons.pending),
  queued: entry('В очереди', 'info', statusIcons.info),
  approvalrequired: entry('Требует согласования', 'warning', statusIcons.warning),
  approved: entry('Согласовано', 'success', statusIcons.success),
  posting: entry('Проведение', 'info', statusIcons.info),
  sending: entry('Отправка', 'info', statusIcons.info),
  posted: entry('Проведено', 'info', statusIcons.info),
  readbackpending: entry('Ожидает readback', 'info', statusIcons.info),
  reconciled: entry('Сверено', 'success', statusIcons.success),
  failed: entry('Ошибка', 'danger', statusIcons.error),
  rejected: entry('Отклонено', 'danger', statusIcons.error),
  retryscheduled: entry('Повтор запланирован', 'warning', statusIcons.warning),
  cancelled: entry('Отменено', 'neutral', statusIcons.neutral),
  cancelledbyharddelete: entry('Удалено', 'neutral', statusIcons.neutral),
  skipped: entry('Пропущено', 'neutral', statusIcons.neutral),
  paymentwritedisabled: entry('Запись отключена', 'warning', statusIcons.warning),
};

const allocationStatusMap: Record<string, StatusUiModel> = {
  draft: entry('Черновик', 'neutral', statusIcons.pending),
  unallocated: entry('Не распределено', 'warning', statusIcons.warning),
  partial: entry('Частично', 'warning', statusIcons.warning),
  allocated: entry('Распределено', 'success', statusIcons.success),
  preview: entry('Предпросмотр', 'info', statusIcons.info),
  suggested: entry('Предложено', 'info', statusIcons.info),
  needsreview: entry('Требует проверки', 'warning', statusIcons.warning),
  confirmed: entry('Подтверждено', 'success', statusIcons.success),
  approvalrequired: entry('Требует согласования', 'warning', statusIcons.warning),
  approved: entry('Согласовано', 'success', statusIcons.success),
  postingqueued: entry('В очереди проведения', 'info', statusIcons.info),
  posted: entry('Проведено', 'info', statusIcons.info),
  reconciled: entry('Сверено', 'success', statusIcons.success),
  failed: entry('Ошибка', 'danger', statusIcons.error),
  cancelled: entry('Отменено', 'neutral', statusIcons.neutral),
};

const reconciliationStatusMap: Record<string, StatusUiModel> = {
  open: entry('Открыто', 'warning', statusIcons.warning),
  matched: entry('Сопоставлено', 'success', statusIcons.success),
  discrepancy: entry('Расхождение', 'danger', statusIcons.error),
  closed: entry('Закрыто', 'neutral', statusIcons.neutral),
};

const importStatusMap: Record<string, StatusUiModel> = {
  pending: entry('Ожидание', 'neutral', statusIcons.pending),
  processing: entry('Обработка', 'info', statusIcons.info),
  completed: entry('Завершено', 'success', statusIcons.success),
  failed: entry('Ошибка', 'danger', statusIcons.error),
  needsreview: entry('Требует проверки', 'warning', statusIcons.warning),
};

const recognitionStatusMap: Record<string, StatusUiModel> = {
  pending: entry('Ожидание', 'neutral', statusIcons.pending),
  processing: entry('Распознавание', 'info', statusIcons.info),
  needsreview: entry('На проверке', 'warning', statusIcons.warning),
  confirmed: entry('Подтверждено', 'success', statusIcons.success),
  rejected: entry('Отклонено', 'danger', statusIcons.error),
  failed: entry('Ошибка', 'danger', statusIcons.error),
};

function normalizeKey(status: string | undefined): string {
  return (status ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function resolveFromMap(
  map: Record<string, StatusUiModel>,
  status: string | undefined,
  fallbackLabel?: string,
): StatusUiModel {
  const key = normalizeKey(status);
  const mapped = map[key];
  if (mapped) {
    return fallbackLabel ? { ...mapped, label: fallbackLabel } : mapped;
  }
  return entry(fallbackLabel ?? status ?? '—', 'neutral', statusIcons.neutral);
}

export function resolvePaymentStatusUi(status: string | undefined, fallbackLabel?: string): StatusUiModel {
  return resolveFromMap(paymentStatusMap, status, fallbackLabel);
}

export function resolvePostingStatusUi(status: string | undefined, fallbackLabel?: string): StatusUiModel {
  return resolveFromMap(postingStatusMap, status, fallbackLabel);
}

export function resolveAllocationStatusUi(status: string | undefined, fallbackLabel?: string): StatusUiModel {
  return resolveFromMap(allocationStatusMap, status, fallbackLabel);
}

export function resolveReconciliationStatusUi(status: string | undefined, fallbackLabel?: string): StatusUiModel {
  return resolveFromMap(reconciliationStatusMap, status, fallbackLabel);
}

export function resolveImportStatusUi(status: string | undefined, fallbackLabel?: string): StatusUiModel {
  return resolveFromMap(importStatusMap, status, fallbackLabel);
}

export function resolveRecognitionStatusUi(status: string | undefined, fallbackLabel?: string): StatusUiModel {
  return resolveFromMap(recognitionStatusMap, status, fallbackLabel);
}

/** Backward-compatible map shape used by older callers. */
export const paymentStatusUiMap: Record<string, { label: string; tone: StatusTone }> = Object.fromEntries(
  Object.entries(paymentStatusMap).map(([key, value]) => [key, { label: value.label, tone: value.tone }]),
);
